/**
 * Phase 8.2 — Tutor Learning Resources tab.
 *
 * Composition:
 *   • Student selector (from assigned roster)
 *   • Subject selector (from what the tutor teaches THIS student)
 *   • Progress + Journey snapshot for the selected student
 *   • Topic list + Mock list scoped to student
 *   • Topic-detail drawer with lesson notes, related resources, student's
 *     attempts, weak-topic badge, "Use in Classroom" + "Assign for revision".
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Users, Filter, ChevronRight, X, Sparkles, Award,
  Send, Flame, Trophy, Loader2, GraduationCap, ExternalLink, TrendingUp,
  Video, Coins, Shield,
} from 'lucide-react';
import QuestionVisual from '../shared/QuestionVisual';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const DIFF_COLOR = {
  Easy: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Hard: 'bg-rose-50 text-rose-700 border-rose-200',
};

const StudentCard = ({ s, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-3 rounded-xl border transition-colors ${selected ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'}`}
    data-testid={`tl-student-${s.student_email}`}>
    <div className="text-sm font-bold text-gray-900 truncate">{s.student_name || s.student_email}</div>
    <div className="text-[10px] text-gray-500">{s.student_email}</div>
    <div className="flex flex-wrap gap-1 mt-2">
      {(s.subjects || []).map((sub) => (
        <span key={sub} className="text-[10px] px-2 py-0.5 rounded-full bg-black text-[#D3D86F] font-bold uppercase tracking-wider">{sub}</span>
      ))}
      {(s.programs || []).map((p) => (
        <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-semibold">{p}</span>
      ))}
    </div>
  </button>
);

const ProgressCard = ({ progress, journey }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="tl-progress">
    {[
      { label: 'XP', value: progress?.total_xp ?? 0, icon: Sparkles, tone: 'bg-sky-50 text-sky-800 border-sky-200' },
      { label: 'Streak', value: `${progress?.streak_days ?? 0}d`, icon: Flame, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
      { label: 'Topics done', value: progress?.topics_completed ?? 0, icon: BookOpen, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
      { label: 'Mocks', value: progress?.mocks_taken ?? 0, icon: Trophy, tone: 'bg-rose-50 text-rose-800 border-rose-200' },
    ].map((s) => (
      <div key={s.label} className={`rounded-xl p-3 border ${s.tone}`}>
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
          <s.icon className="h-3 w-3" /> {s.label}
        </div>
        <div className="text-xl font-bold mt-1">{s.value}</div>
      </div>
    ))}
    {journey && (
      <div className="col-span-2 md:col-span-4 bg-black text-white rounded-xl p-3 flex items-center gap-3" data-testid="tl-journey-snapshot">
        <TrendingUp className="h-5 w-5 text-[#D3D86F] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#D3D86F] font-bold">Journey · {journey.stages_completed}/{journey.stages_total} · {journey.overall_pct}%</div>
          <div className="font-bold text-sm truncate">Current stage: {journey.current_stage_title}</div>
        </div>
        <div className="h-2 w-32 rounded-full bg-white/10 overflow-hidden shrink-0">
          <div className="h-full bg-[#D3D86F]" style={{ width: `${journey.overall_pct}%` }} />
        </div>
      </div>
    )}
  </div>
);

const TopicRow = ({ t, onOpen }) => (
  <button onClick={onOpen} data-testid={`tl-topic-${t.slug}`}
    className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-red-500 hover:bg-red-50 transition-colors flex items-center gap-3">
    <BookOpen className="h-4 w-4 text-red-700 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-bold text-gray-900 truncate">{t.title}</div>
      <div className="text-[10px] text-gray-500 flex items-center gap-2">
        <span className={`inline-block px-2 py-0.5 rounded-full border text-[9px] font-bold ${DIFF_COLOR[t.difficulty] || ''}`}>{t.difficulty}</span>
        <span>{t.duration_minutes || 20} min</span>
        {t.student_attempts > 0 && <span className="text-emerald-700 font-bold">Student: {t.student_attempts} attempt{t.student_attempts === 1 ? '' : 's'}</span>}
      </div>
    </div>
    <ChevronRight className="h-4 w-4 text-gray-400" />
  </button>
);

const TopicDrawer = ({ studentEmail, topicSlug, onClose, onAssign }) => {
  const [d, setD] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [noRoomModal, setNoRoomModal] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get(`/api/me/tutor/learning/student/${studentEmail}/topic/${topicSlug}`);
        setD(r.data);
      } catch (_e) { toast.error('Could not load topic'); }
    })();
  }, [studentEmail, topicSlug]);
  const assign = async () => {
    setBusy(true);
    try {
      const r = await ax.post(`/api/me/tutor/learning/student/${studentEmail}/assign-revision`, {
        resource_kind: 'topic',
        resource_slug: topicSlug,
        resource_title: d?.topic?.title || topicSlug,
        track_slug: d?.topic?.track_slug,
        note,
      });
      if (r.data.duplicate) toast.info('This revision was already assigned — reusing the pending task.');
      else toast.success(`Revision assigned to ${studentEmail}`);
      onAssign?.(r.data.task);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };
  const useInClassroom = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/me/tutor/classroom/open', {
        student_email: studentEmail,
        resource_kind: 'topic',
        resource_slug: topicSlug,
        resource_title: d?.topic?.title || topicSlug,
        track_slug: d?.topic?.track_slug,
      });
      if (r.data.has_booking) {
        toast.success(`Opening classroom — ${r.data.is_live ? 'live now' : 'starts ' + new Date(r.data.start_time).toLocaleString()}`);
        window.open(r.data.join_url, '_blank');
      } else {
        setNoRoomModal(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open classroom');
    } finally { setBusy(false); }
  };
  const copyResourceLink = () => {
    const link = `${window.location.origin}/student/learning?topic=${encodeURIComponent(topicSlug)}`;
    navigator.clipboard.writeText(link);
    toast.success('Resource link copied');
  };
  if (!d) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="tl-topic-drawer">
      <div className="bg-white rounded-2xl p-10"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>
    </div>
  );
  const t = d.topic;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="tl-topic-drawer">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">{t.track_slug} · {t.difficulty}</div>
            <div className="font-bold text-lg truncate">{t.title}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="tl-drawer-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Lesson notes */}
          {(t.overview || t.description) && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Overview</div>
              <p className="text-sm text-gray-800 whitespace-pre-line">{t.overview || t.description}</p>
            </div>
          )}
          {/* Lesson content */}
          {t.lesson_notes && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Lesson notes</div>
              <div className="prose prose-sm max-w-none whitespace-pre-line">{t.lesson_notes}</div>
            </div>
          )}
          {t.worked_examples && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Worked examples</div>
              <div className="prose prose-sm max-w-none whitespace-pre-line">{typeof t.worked_examples === 'string' ? t.worked_examples : JSON.stringify(t.worked_examples, null, 2)}</div>
            </div>
          )}
          {t.common_mistakes && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Common mistakes</div>
              <div className="prose prose-sm max-w-none whitespace-pre-line">{typeof t.common_mistakes === 'string' ? t.common_mistakes : (t.common_mistakes || []).join('\n• ')}</div>
            </div>
          )}
          {t.revision_summary && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Revision summary</div>
              <div className="prose prose-sm max-w-none whitespace-pre-line">{t.revision_summary}</div>
            </div>
          )}

          {/* Student's progress */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3" data-testid="tl-student-progress">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Student's progress</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-xs text-gray-500">Attempts</div><div className="text-lg font-bold">{d.student_progress.attempts_count}</div></div>
              <div><div className="text-xs text-gray-500">Best score</div><div className="text-lg font-bold">{d.student_progress.best_score || 0}%</div></div>
              <div><div className="text-xs text-gray-500">Weak?</div><div className="text-lg font-bold">{d.student_progress.is_weak ? 'Yes' : 'No'}</div></div>
            </div>
          </div>

          {/* Related resources */}
          {d.related_resources?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Related resources</div>
              <ul className="space-y-1">
                {d.related_resources.map((r) => (
                  <li key={r.slug || r.title}><a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm text-red-700 hover:underline inline-flex items-center gap-1">{r.title} <ExternalLink className="h-3 w-3" /></a></li>
                ))}
              </ul>
            </div>
          )}

          {/* Practice questions preview (count) */}
          {(t.question_count || t.practice_count) && (
            <div className="text-xs text-gray-600">
              <BookOpen className="inline h-3 w-3 mr-1" /> {t.question_count || t.practice_count} practice questions available in this topic.
            </div>
          )}

          {/* Sample questions with visuals */}
          {d.questions && d.questions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Sample questions ({d.questions.length})</div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1" data-testid="tl-questions-preview">
                {d.questions.slice(0, 5).map((q, i) => (
                  <div key={q.id} className="rounded-xl border border-gray-200 p-3" data-testid={`tl-q-${q.id}`}>
                    <QuestionVisual visual_type={q.visual_type} visual_payload={q.visual_payload}
                      visual_caption={q.visual_caption} visual_alt_text={q.visual_alt_text} />
                    <div className="text-sm font-medium text-gray-900">{i + 1}. {q.prompt}</div>
                    {q.kind === 'mcq' && q.choices && (
                      <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                        {q.choices.map((c, ci) => (
                          <div key={ci} className="text-xs border rounded-lg px-2.5 py-1.5 border-gray-200">
                            {String.fromCharCode(65 + ci)}. {c}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assign for revision */}
          <div className="rounded-xl border border-red-200 bg-red-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold mb-1">Assign for revision</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note the student will see (optional)"
              rows={2}
              data-testid="tl-assign-note"
              className="w-full text-xs px-3 py-2 rounded-lg border border-red-200 focus:outline-none focus:border-red-500 bg-white" />
            <div className="flex flex-wrap gap-2 mt-2">
              <button onClick={assign} disabled={busy}
                data-testid="tl-assign-btn"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                <Send className="h-3 w-3" /> Assign for Revision
              </button>
              <button onClick={useInClassroom} disabled={busy}
                data-testid="tl-use-in-classroom-btn"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-black hover:bg-gray-800 text-[#D3D86F] text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                <Video className="h-3 w-3" /> Use in Classroom
              </button>
            </div>
          </div>
        </div>
      </div>

      {noRoomModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="tl-no-room-modal"
          onClick={() => setNoRoomModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Classroom not scheduled</div>
            <div className="text-lg font-bold text-black mt-1">No upcoming classroom found</div>
            <p className="text-sm text-gray-700 mt-2">
              There is no active or upcoming classroom booking for
              <b className="mx-1">{studentEmail}</b> on this subject. Create a
              booking or open their timetable to schedule one, then click
              <b> Use in Classroom </b> again — the topic will attach automatically.
            </p>
            <div className="grid grid-cols-1 gap-2 mt-4">
              <a href={noRoomModal.create_booking_url || '/tutor/dashboard'} target="_blank" rel="noreferrer"
                className="h-10 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xs uppercase tracking-wider font-black inline-flex items-center justify-center"
                data-testid="tl-no-room-create-booking">
                Create a booking
              </a>
              <a href={noRoomModal.timetable_url || '/tutor/dashboard'} target="_blank" rel="noreferrer"
                className="h-10 rounded-lg bg-black hover:bg-gray-800 text-[#D3D86F] text-xs uppercase tracking-wider font-black inline-flex items-center justify-center"
                data-testid="tl-no-room-timetable">
                Open student timetable
              </a>
              <button onClick={copyResourceLink}
                className="h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-black text-xs uppercase tracking-wider font-black"
                data-testid="tl-no-room-copy-link">
                Copy resource link
              </button>
              <button onClick={() => setNoRoomModal(null)}
                className="h-9 text-xs text-gray-500 hover:text-black"
                data-testid="tl-no-room-close">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TutorLearningResources = () => {
  const [students, setStudents] = useState([]);
  const [selEmail, setSelEmail] = useState(null);
  const [track, setTrack] = useState(null);
  const [topics, setTopics] = useState([]);
  const [mocks, setMocks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [economy, setEconomy] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await ax.get('/api/me/tutor/learning/students');
        setStudents(r.data.items || []);
        if ((r.data.items || []).length > 0) {
          setSelEmail(r.data.items[0].student_email);
        }
      } catch (_e) { toast.error('Could not load your students'); }
      finally { setLoading(false); }
    })();
  }, []);

  const selStudent = useMemo(() => students.find(s => s.student_email === selEmail), [students, selEmail]);
  const availableTracks = selStudent?.subjects || [];

  useEffect(() => {
    if (!selEmail) return;
    // Default track to the first assigned subject
    if (!track && availableTracks.length > 0) {
      setTrack(availableTracks[0]);
      return;
    }
    if (!track) return;
    (async () => {
      try {
        const [tRes, mRes, pRes] = await Promise.all([
          ax.get(`/api/me/tutor/learning/student/${selEmail}/topics?track=${track}`),
          ax.get(`/api/me/tutor/learning/student/${selEmail}/mocks`),
          ax.get(`/api/me/tutor/learning/student/${selEmail}/progress`),
        ]);
        setTopics(tRes.data.items || []);
        setMocks(mRes.data.items || []);
        setProgress(pRes.data);
        try {
          const eRes = await ax.get(`/api/me/tutor/economy/student/${selEmail}`);
          setEconomy(eRes.data);
        } catch (_e) { setEconomy(null); }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load student learning data');
        setTopics([]); setMocks([]);
      }
    })();
  }, [selEmail, track, availableTracks]);

  if (loading) {
    return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>;
  }
  if (students.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-testid="tl-empty">
        <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        <div className="text-sm font-bold text-gray-800">No assigned students yet</div>
        <div className="text-xs text-gray-500 mt-1">Once you have classes with students, their Learning Centre resources will appear here.</div>
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4" data-testid="tutor-learning-resources">
      {/* Left column: student picker */}
      <aside className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 px-1">Your students · {students.length}</div>
        {students.map((s) => (
          <StudentCard key={s.student_email} s={s}
            selected={s.student_email === selEmail}
            onClick={() => { setSelEmail(s.student_email); setTrack(null); }} />
        ))}
      </aside>

      {/* Right column: selected student's resources */}
      <section className="space-y-4">
        {selStudent && (
          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Student</div>
                <div className="font-bold text-base">{selStudent.student_name}</div>
                <div className="text-[11px] text-gray-500">{selStudent.student_email}</div>
              </div>
              <div className="flex flex-wrap gap-1 items-center">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mr-2 inline-flex items-center gap-1"><Filter className="h-3 w-3" /> Subject</span>
                {availableTracks.map(sub => (
                  <button key={sub} onClick={() => setTrack(sub)}
                    data-testid={`tl-subject-${sub}`}
                    className={`text-[11px] font-bold uppercase tracking-wider h-8 px-3 rounded-full border ${track === sub ? 'bg-black text-[#D3D86F] border-black' : 'bg-white text-gray-700 border-gray-200 hover:border-black'}`}>
                    {sub}
                  </button>
                ))}
              </div>
            </div>
            <ProgressCard progress={progress} journey={progress?.journey} />
            {economy && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="tl-economy-snapshot">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-amber-800 font-bold flex items-center gap-1"><Coins className="h-3 w-3" /> WinCoins</div>
                  <div className="text-base font-black text-amber-900 mt-0.5" data-testid="tl-eco-coins">{economy.wallet?.balance || 0}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-red-800 font-bold flex items-center gap-1"><Shield className="h-3 w-3" /> House</div>
                  <div className="text-sm font-black text-red-900 mt-0.5 capitalize" data-testid="tl-eco-house">{economy.profile?.house || '—'}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-blue-800 font-bold flex items-center gap-1"><Sparkles className="h-3 w-3" /> XP</div>
                  <div className="text-base font-black text-blue-900 mt-0.5" data-testid="tl-eco-xp">{economy.xp || 0}</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-wider text-orange-800 font-bold flex items-center gap-1"><Flame className="h-3 w-3" /> Streak</div>
                  <div className="text-base font-black text-orange-900 mt-0.5" data-testid="tl-eco-streak">{economy.streak_days || 0}d</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Topic list */}
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5"><BookOpen className="h-3 w-3" /> Topics · {topics.length}</div>
            {track && <div className="text-[10px] text-gray-500">Subject: <span className="font-bold text-black uppercase">{track}</span></div>}
          </div>
          {topics.length === 0 ? (
            <div className="text-xs text-gray-400 italic">No topics available for this subject.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-2" data-testid="tl-topic-list">
              {topics.map((t) => (
                <TopicRow key={t.slug} t={t} onOpen={() => setOpenTopic(t.slug)} />
              ))}
            </div>
          )}
        </div>

        {/* Mocks + weak-topic sidebar */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5 mb-3"><Trophy className="h-3 w-3" /> Mocks · {mocks.length}</div>
            {mocks.length === 0 && <div className="text-xs text-gray-400 italic">No mocks available.</div>}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="tl-mock-list">
              {mocks.slice(0, 20).map((m) => (
                <div key={m.slug} className="p-2 rounded-lg border border-gray-100 flex items-center gap-2" data-testid={`tl-mock-${m.slug}`}>
                  <GraduationCap className="h-3 w-3 text-red-700 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs font-semibold truncate">{m.title || m.slug}</div>
                  {m.difficulty && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${DIFF_COLOR[m.difficulty] || ''}`}>{m.difficulty}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5 mb-3"><Award className="h-3 w-3" /> Weak topics</div>
            {(!progress?.weakest_topics || progress.weakest_topics.length === 0) ? (
              <div className="text-xs text-gray-400 italic">No weak-topic data yet.</div>
            ) : (
              <ul className="space-y-1.5" data-testid="tl-weak-list">
                {progress.weakest_topics.slice(0, 6).map((w, i) => {
                  const label = typeof w === 'string' ? w : (w.title || w.slug);
                  const slug = typeof w === 'string' ? w : w.slug;
                  return (
                    <li key={slug || i}>
                      <button onClick={() => slug && setOpenTopic(slug)}
                        className="text-sm text-red-700 hover:underline inline-flex items-center gap-1">
                        <ChevronRight className="h-3 w-3" /> {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {openTopic && (
        <TopicDrawer studentEmail={selEmail} topicSlug={openTopic}
          onClose={() => setOpenTopic(null)}
          onAssign={() => { /* let the toast handle it */ }} />
      )}
    </div>
  );
};

export default TutorLearningResources;
